/**
 * 생육 화면의 관부 등록 진입 — 장비 사진이 LRM 관부 등록·측정 설정을 기다리면 표시와 버튼을 띄운다.
 *
 * 버튼은 기존 LrmRegistrationModal 을 연다. 등록이 끝나면 API 가 그 기록의 lrm 상태를 비워 다음 제출 주기
 * (최대 5분)에 같은 사진이 다시 LRM 에 들어간다 — 그동안 기록을 30초마다 다시 읽어 처리 결과를 받는다.
 * 등록이 필요 없는 장비(기록 모두 정상)에서는 아무것도 그리지 않는다.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Sym } from '../../../ui';
import { useQueryClient } from '@tanstack/react-query';
import type { GrowthRecord } from '@grosync/common';
import { lrmRegistrationNeeded } from '../../../api/growth';
import { LrmRegistrationModal } from './LrmRegistrationModal';

/** 등록 뒤 처리 결과를 기다리는 동안 기록을 다시 읽는 간격 · 최대 기다리는 시간 */
const POLL_MS = 30_000;
const POLL_MAX_MS = 30 * 60_000;

type LrmView = { publishedAt?: string | null; status?: string | null; error?: { code?: string; message?: string } | null };
const lrmOf = (r: GrowthRecord | undefined): LrmView | null =>
  ((r?.aiSuggestions as { lrm?: LrmView } | null)?.lrm ?? null);

/** 등록 뒤 상태 — 기다림(재제출 전·처리 중) · 완료 · 다시 실패 */
export function afterRegistration(r: GrowthRecord | undefined): 'waiting' | 'done' | 'failed' {
  const lrm = lrmOf(r);
  if (lrm?.publishedAt) return 'done';
  if (lrm?.status === 'failed' || lrm?.error) return 'failed';
  return 'waiting';
}

export function LrmRegistrationNotice({ records }: { records: GrowthRecord[] }) {
  const queryClient = useQueryClient();
  const [registering, setRegistering] = useState<GrowthRecord | null>(null);
  const [waiting, setWaiting] = useState<{ id: string; since: number } | null>(null);

  const pending = records.find((r) => lrmRegistrationNeeded(r)); // 최신 사진부터 (records 는 newest-first)
  const watched = waiting ? records.find((r) => r.id === waiting.id) : undefined;
  const state = waiting ? afterRegistration(watched) : null;
  // 등록은 됐는데 최신 사진 처리가 실패 — 예: 찍은 꼭짓점이 기준 물체 QC(재투영 오차)에 걸림. 다시 등록으로 고칠 수 있게
  const latest = records[0];
  const failed = !pending && !waiting && latest && afterRegistration(latest) === 'failed' ? latest : undefined;
  const [reregister, setReregister] = useState(false);
  const openRegistration = (r: GrowthRecord, again: boolean): void => {
    setReregister(again);
    setRegistering(r);
  };
  const reregisterButton = (r: GrowthRecord) => (
    <ActionBtn icon="edit_location_alt" onClick={() => openRegistration(r, true)}>관부·기준물체 다시 등록</ActionBtn>
  );

  useEffect(() => {
    if (!waiting || state !== 'waiting') return;
    const t = setInterval(() => {
      if (Date.now() - waiting.since > POLL_MAX_MS) {
        setWaiting(null);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['growth-records'] });
    }, POLL_MS);
    return () => clearInterval(t);
  }, [waiting, state, queryClient]);

  if (!pending && !waiting && !failed) return null;

  return (
    <>
      {pending ? (
        <Banner tone="warn" icon="pin_drop"
                title={lrmRegistrationNeeded(pending) === 'not_registered' ? '관부 등록 필요' : '측정 설정 필요'}
                action={<ActionBtn icon="pin_drop" onClick={() => openRegistration(pending, false)}>관부 등록</ActionBtn>}>
          {pending.recordedAt.slice(0, 10)} 사진을 분석하려면 이 장비의 관부를 한 번 등록해야 합니다.
        </Banner>
      ) : failed ? (
        <Banner tone="error" icon="error" title="분석 실패" action={reregisterButton(failed)}>
          {failed.recordedAt.slice(0, 10)} 사진 — {lrmOf(failed)?.error?.message ?? lrmOf(failed)?.error?.code ?? '알 수 없는 오류'}
        </Banner>
      ) : state === 'waiting' ? (
        <Banner tone="info" icon="hourglass_top" title="등록 완료">
          {watched?.recordedAt.slice(0, 10)} 사진을 자동으로 다시 분석하는 중입니다 (보통 5~10분) — 끝나면 이 화면에 나옵니다.
        </Banner>
      ) : state === 'done' ? (
        <Banner tone="success" icon="check_circle" title="분석 완료" onClose={() => setWaiting(null)}>
          관부 등록 후 {watched?.recordedAt.slice(0, 10)} 사진 분석이 끝났습니다.
        </Banner>
      ) : (
        <Banner tone="error" icon="error" title="재분석 실패" onClose={() => setWaiting(null)}
                action={watched ? reregisterButton(watched) : undefined}>
          {lrmOf(watched)?.error?.message ?? lrmOf(watched)?.error?.code ?? '알 수 없는 오류'}
        </Banner>
      )}
      {registering && (
        <LrmRegistrationModal
          record={registering}
          reregister={reregister}
          onClose={() => setRegistering(null)}
          onRegistered={() => {
            setWaiting({ id: registering.id, since: Date.now() });
            void queryClient.invalidateQueries({ queryKey: ['growth-records'] });
          }}
        />
      )}
    </>
  );
}

/* ---- 안내 띠 — 3D 분석 화면의 상태 띠와 같은 모양 (토큰 색 · 아이콘 · 13px 본문 · 알약 버튼) ---- */

type Tone = 'warn' | 'error' | 'info' | 'success';
/** 상태색 — 밝은·어두운 화면 모두에서 보이는 색 (컨테이너 색은 어두운 화면에서 바뀌지 않아 쓰지 않는다) */
const TONE: Record<Tone, string> = {
  warn: 'var(--gs-warning)',
  error: 'var(--md-sys-color-error)',
  info: 'var(--md-sys-color-on-surface-variant)',
  success: 'var(--gs-success)',
};
/** 패널 바탕에 상태색을 조금 섞은 바탕 · 얇은 상태색 테두리 */
export const toneSurface = (c: string) => ({
  background: `color-mix(in srgb, ${c} 10%, var(--md-sys-color-surface-container-lowest))`,
  border: `1px solid color-mix(in srgb, ${c} 45%, transparent)`,
});

function Banner({ tone, icon, title, children, action, onClose }: {
  tone: Tone; icon: string; title: string; children: ReactNode; action?: ReactNode; onClose?: () => void;
}) {
  const c = TONE[tone];
  return (
    <div role={tone === 'error' || tone === 'warn' ? 'alert' : 'status'}
         style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 16px',
                  borderRadius: 16, ...toneSurface(c), color: 'var(--md-sys-color-on-surface)', font: '500 13px/19px var(--gs-font-kr)' }}>
      <Sym name={icon} size={20} fill={1} style={{ color: c }} />
      <strong style={{ font: '700 13px/19px var(--gs-font-kr)', color: c }}>{title}</strong>
      <span style={{ flex: 1, minWidth: 200 }}>{children}</span>
      {action}
      {onClose && (
        <button type="button" aria-label="닫기" onClick={onClose}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', display: 'inline-flex' }}>
          <Sym name="close" size={18} />
        </button>
      )}
    </div>
  );
}

function ActionBtn({ icon, onClick, children }: { icon: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick}
            style={{ height: 36, padding: '0 15px', borderRadius: 9999, cursor: 'pointer', background: 'transparent',
                     border: '1px solid var(--md-sys-color-outline)', color: 'var(--md-sys-color-on-surface)',
                     font: '600 13px/1 var(--gs-font-kr)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Sym name={icon} size={17} />
      {children}
    </button>
  );
}
